import {Button} from "primereact/button";
import {Menu} from "primereact/menu";
import type {MenuItem} from "primereact/menuitem";
import {type ComponentType, type SVGProps, useRef} from "react";
import {useTranslation} from "react-i18next";

import ThemeAutoIcon from "../images/theme_auto.svg?react";
import ThemeDarkIcon from "../images/theme_dark.svg?react";
import ThemeLightIcon from "../images/theme_light.svg?react";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {selectCurrentTheme, THEME_AUTO, THEME_DARK, THEME_LIGHT, type Theme} from "../redux/themeSlice";
import {setTheme} from "../redux/themeThunk";

interface ThemeMenuItem {
    value: Theme;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    label: string;
}

// The icons are decorative: every menu entry carries its own text label, and the button
// takes its accessible name from aria-label.
const themeIconProps = {"aria-hidden": true, width: "1em", height: "1em"} as const;

function ThemeSwitcher() {
    const dispatch = useRevaDispatch();
    const {t} = useTranslation();

    const themeSwitcherMenu = useRef<Menu | null>(null);

    // Current Theme Name: THEME_LIGHT / THEME_DARK / THEME_AUTO
    const currentTheme = useRevaSelector(selectCurrentTheme);

    const themeItems: ThemeMenuItem[] = [
        {
            value: THEME_LIGHT,
            Icon: ThemeLightIcon,
            label: t("theme.light"),
        },
        {
            value: THEME_DARK,
            Icon: ThemeDarkIcon,
            label: t("theme.dark"),
        },
        {
            value: THEME_AUTO,
            Icon: ThemeAutoIcon,
            label: t("theme.auto"),
        },
    ];
    let currentThemeItem = themeItems.filter((item) => item.value === currentTheme)[0];
    if (!currentThemeItem) {
        console.error(`Invalid theme item: ${currentTheme}`);
        currentThemeItem = themeItems[0];
    }
    const CurrentThemeIcon = currentThemeItem.Icon;

    const model: MenuItem[] = themeItems.map(({label, Icon, value}) => {
        return {
            label,
            icon: <Icon {...themeIconProps} className="mr-2" />,
            command: () => dispatch(setTheme(value)),
        };
    });

    return (
        <>
            <Menu id="themeSwitcherMenu" model={model} popup ref={themeSwitcherMenu} popupAlignment="right" />
            <Button
                id="themeSwitcherButton"
                icon={<CurrentThemeIcon {...themeIconProps} />}
                className="ml-1"
                onClick={(e) => themeSwitcherMenu.current?.toggle(e)}
                aria-label={t("theme.switch", {theme: currentThemeItem.label})}
                aria-controls="themeSwitcherMenu"
                aria-haspopup
                text
                size="small"
            />
        </>
    );
}

export default ThemeSwitcher;
